// src/config/geography.js
//
// States and grades, in one place. These used to be declared inside
// containers/navbar/login.js, which meant the second signup form on
// /register had its own copy — and the two didn't agree.
//
// The disagreement mattered: the navbar form stored grade as "3rd", the
// /register form stored "3". Role strings are composed as
// `${state}-${grade}-${role}` ("va-3rd-educator"), and PrivateRoute matches
// those literally, so an account registered through the wrong door got
// "va-3-educator" and matched no route in the app.

export const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL",
  "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT",
  "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI",
  "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY", "DC",
];

// Full names. The abbreviation is what goes in role strings and URLs; this is
// what a parent reads in a dropdown. "MI" and "MN" and "MS" and "MO" are four
// different states and a tired person at 11pm will pick the wrong one.
export const STATE_NAMES = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas",
  CA: "California", CO: "Colorado", CT: "Connecticut", DE: "Delaware",
  DC: "District of Columbia", FL: "Florida", GA: "Georgia", HI: "Hawaii",
  ID: "Idaho", IL: "Illinois", IN: "Indiana", IA: "Iowa", KS: "Kansas",
  KY: "Kentucky", LA: "Louisiana", ME: "Maine", MD: "Maryland",
  MA: "Massachusetts", MI: "Michigan", MN: "Minnesota", MS: "Mississippi",
  MO: "Missouri", MT: "Montana", NE: "Nebraska", NV: "Nevada",
  NH: "New Hampshire", NJ: "New Jersey", NM: "New Mexico", NY: "New York",
  NC: "North Carolina", ND: "North Dakota", OH: "Ohio", OK: "Oklahoma",
  OR: "Oregon", PA: "Pennsylvania", RI: "Rhode Island", SC: "South Carolina",
  SD: "South Dakota", TN: "Tennessee", TX: "Texas", UT: "Utah",
  VT: "Vermont", VA: "Virginia", WA: "Washington", WV: "West Virginia",
  WI: "Wisconsin", WY: "Wyoming",
};

export const stateName = (code) =>
  STATE_NAMES[String(code || "").toUpperCase()] || String(code || "").toUpperCase();

// ---------------------------------------------------------------------------
// SCIENCE STANDARDS
// ---------------------------------------------------------------------------
//
// Three buckets, and the difference is not cosmetic — it decides what code we
// print next to a topic in the educator view.
//
// NGSS_ADOPTED (20 states + DC) adopted the Next Generation Science Standards
// verbatim. Their teachers, pacing guides and district assessments all use the
// literal PE code: "3-PS2-1". We can print that code and it is correct.
//
// NGSS_FRAMEWORK (24 states) wrote their own standards from the same NRC
// Framework for K-12 Science Education. The science is close enough that the
// same song teaches the same idea — but the CODE is different. Utah writes
// SEEd "5.1.1", Georgia writes GSE "S3P1", Alabama writes "SC.3.1". Printing
// "3-PS2-1" to an Alabama teacher is printing a code her district has never
// used, and it makes the product look like it doesn't know her state.
//
// So framework states get the NGSS *content* and a crosswalk note, not a
// borrowed code, until we load their real code tables. `scienceStandardSet()`
// is what the portal should branch on.
//
// NEITHER (FL, NC, OH, PA, TX, VA) keep their own lineage — VA SOL, NC
// Essential Standards, TEKS. Those already have hand-built topic files and
// must not be pointed at the NGSS library.
export const NGSS_ADOPTED = [
  "AR", "CA", "CT", "DE", "DC", "HI", "IL", "IA", "KS", "KY", "ME", "MD",
  "MI", "NV", "NH", "NJ", "NM", "OR", "RI", "VT", "WA",
];

export const NGSS_FRAMEWORK = [
  "AL", "AK", "AZ", "CO", "GA", "ID", "IN", "LA", "MA", "MN", "MS", "MO",
  "MT", "NE", "NY", "ND", "OK", "SC", "SD", "TN", "UT", "WV", "WI", "WY",
];

// Every state that reads from src/data/ngss-science TODAY.
//
// Adopted only, on purpose. A framework state gets the NGSS content right and
// the standard CODE wrong, and the code is the thing an educator checks
// against her pacing guide. Better to ship nothing there than ship a number
// her district has never used. Move a framework state into this list the day
// its real code table is loaded.
export const NGSS_STATES = [...NGSS_ADOPTED];

// "ngss" | "ngss-framework" | "state"
export const scienceStandardSet = (state) => {
  const s = String(state || "").toUpperCase();
  if (NGSS_ADOPTED.includes(s)) return "ngss";
  if (NGSS_FRAMEWORK.includes(s)) return "ngss-framework";
  return "state";
};

export const usesNgssScience = (state) =>
  scienceStandardSet(state) !== "state";

// What to show beside a standard code in the educator view. Framework states
// get told plainly that the code is the NGSS equivalent, not their state's own
// — an honest label beats a wrong one.
export const scienceStandardLabel = (state) => {
  switch (scienceStandardSet(state)) {
    case "ngss":
      return "NGSS";
    case "ngss-framework":
      return `NGSS equivalent — ${stateName(state)} uses its own codes`;
    default:
      return `${stateName(state)} standards`;
  }
};

// ---------------------------------------------------------------------------
// MATH STANDARDS
// ---------------------------------------------------------------------------
//
// Same three-bucket shape as science above, and for the same reason: the
// bucket decides what code we print next to a topic in the educator view.
//
// CCSS_MATH_ADOPTED (39 states + DC) teach standards that still use Common
// Core codes. A teacher looks up "3.NF.3d" and finds it. We print it as-is.
//
// Two of them prefix those codes in their own publications — North Carolina
// writes "NC.3.OA.7", New York Next Generation writes "NY-3.OA.1". Identical
// standard, identical content, different label. CCSS_MATH_CODE_PREFIX handles
// that at render time so there is still exactly one copy of every topic.
//
// Worth flagging because it bit us once: NC is REFUSED for science and ADOPTED
// for math. Its science standards are their own lineage; its Standard Course
// of Study for math is Common Core derived. Different subject, different
// answer, and a list copied from science to math would get this wrong.
//
// CCSS_MATH_DERIVED (AL, GA, IN, MO, SC) rebuilt from Common Core but
// renumbered. The math matches; the code does not. Same call as the NGSS
// framework states — content is right, code would be wrong, so they wait.
//
// NEITHER (AK, FL, MN, NE, OK, TX, VA) never adopted CCSS-M or replaced it
// wholesale: Virginia SOL, Texas TEKS, Florida B.E.S.T., Oklahoma OAS. VA has
// real hand-built math content and must not be pointed at the CCSS library.
export const CCSS_MATH_ADOPTED = [
  "AZ", "AR", "CA", "CO", "CT", "DE", "DC", "HI", "ID", "IL", "IA", "KS",
  "KY", "LA", "ME", "MD", "MA", "MI", "MS", "MT", "NV", "NH", "NJ", "NM",
  "NY", "NC", "ND", "OH", "OR", "PA", "RI", "SD", "TN", "UT", "VT", "WA",
  "WV", "WI", "WY",
];

export const CCSS_MATH_DERIVED = ["AL", "GA", "IN", "MO", "SC"];

// Every state that reads from src/data/ccss-math TODAY.
export const CCSS_MATH_STATES = [...CCSS_MATH_ADOPTED];

// States that publish Common Core codes under their own prefix.
export const CCSS_MATH_CODE_PREFIX = { NC: "NC.", NY: "NY-" };

// "ccss-math" | "ccss-math-derived" | "state"
export const mathStandardSet = (state) => {
  const s = String(state || "").toUpperCase();
  if (CCSS_MATH_ADOPTED.includes(s)) return "ccss-math";
  if (CCSS_MATH_DERIVED.includes(s)) return "ccss-math-derived";
  return "state";
};

export const usesCcssMath = (state) => mathStandardSet(state) !== "state";

// The code as that state's own district writes it. Bare CCSS for most,
// prefixed for NC and NY.
export const mathStandardCode = (state, code) =>
  (CCSS_MATH_CODE_PREFIX[String(state || "").toUpperCase()] || "") + code;

export const mathStandardLabel = (state) => {
  switch (mathStandardSet(state)) {
    case "ccss-math":
      return "Common Core";
    case "ccss-math-derived":
      return `Common Core equivalent — ${stateName(state)} uses its own codes`;
    default:
      return `${stateName(state)} standards`;
  }
};

// States with curriculum built and sellable today.
//
// Same idea as GRADES_WITH_CONTENT below, one level up. Every state stays in
// US_STATES because the field has to accept them — an admin creates accounts
// by hand, seat invites land wherever the buyer is, and an out-of-state
// parent is still a lead. What this list decides is whether the marketing
// picker offers a state or shows it greyed out as "coming soon".
//
// Selling a state before its layouts are seeded produces the worst possible
// version of this product: someone pays, lands in the portal, and finds four
// empty subjects. Better to say "not yet" on the way in.
//
// ALL 51, as of the lyrics-to-standards match.
//
// scripts/match-lyrics-to-standards.mjs builds every state's Math, Science
// and Social Studies pages, K-5, against that state's OWN standards — the
// codes printed are the state's, not NGSS or Common Core borrowed. That
// removes the reason these lists were short: the framework and derived states
// were held back because the CODE would have been wrong, and it no longer is.
//
// These constants are only the FALLBACK now. The live answer is
// stateSettings in Firestore (services/stateSettingsService.js), written by
// `state-status.mjs --go-live`, which marks a subject ready only where its
// pages actually exist. If Firestore is unreachable the picker falls back to
// this — every state offered — rather than to the old two-state list, which
// would hide 49 states that are on sale.
export const AVAILABLE_STATES = [...US_STATES];

export const SCIENCE_READY_STATES = [...US_STATES];

export const MATH_READY_STATES = [...US_STATES];

// Nothing is pending any more. Kept exported, empty, so any roadmap copy that
// reads them renders nothing instead of breaking.
export const MATH_PENDING_STATES = [];

export const SCIENCE_PENDING_STATES = [];

export const isStateAvailable = (state) =>
  AVAILABLE_STATES.includes(String(state || "").toUpperCase());

export const isScienceReady = (state) =>
  SCIENCE_READY_STATES.includes(String(state || "").toUpperCase());

export const isMathReady = (state) =>
  MATH_READY_STATES.includes(String(state || "").toUpperCase());

// Both core subjects seeded. This is the honest bar for "science and math
// available" messaging — the intersection, not either list on its own.
export const isCoreReady = (state) =>
  isScienceReady(state) && isMathReady(state);

// Dropdown groups, in the order they should render. Sorted by full name
// inside each group, because a user scans names, not abbreviations.
const byName = (a, b) => stateName(a).localeCompare(stateName(b));

export const STATE_GROUPS = [
  { label: "Available now", codes: [...AVAILABLE_STATES].sort(byName) },
  {
    label: "Science and math available — more subjects coming",
    codes: US_STATES.filter(
      (s) =>
        !AVAILABLE_STATES.includes(s) &&
        isScienceReady(s) &&
        isMathReady(s)
    ).sort(byName),
  },
  {
    label: "Math available — more subjects coming",
    codes: US_STATES.filter(
      (s) =>
        !AVAILABLE_STATES.includes(s) &&
        isMathReady(s) &&
        !isScienceReady(s)
    ).sort(byName),
  },
  {
    label: "Science available — more subjects coming",
    codes: US_STATES.filter(
      (s) =>
        !AVAILABLE_STATES.includes(s) &&
        isScienceReady(s) &&
        !isMathReady(s)
    ).sort(byName),
  },
  {
    label: "Coming soon",
    codes: US_STATES.filter(
      (s) =>
        !AVAILABLE_STATES.includes(s) &&
        !isScienceReady(s) &&
        !isMathReady(s)
    ).sort(byName),
  },
];

// The "1st" / "2nd" / "3rd" convention the rest of the app already uses —
// see the grade parsing in AdminUsers.js and the route role strings.
export const GRADES = [
  "Pre-K",
  "Kindergarten",
  "1st",
  "2nd",
  "3rd",
  "4th",
  "5th",
  "6th",
  "7th",
  "8th",
  "9th",
  "10th",
  "11th",
  "12th",
];

// Grades that currently have curriculum pages built.
//
// Everything above is SELECTABLE — you want those signups, and an account can
// exist for a grade before its content does. This list is what decides
// whether someone is sent to a real portal or told plainly that their grade
// is still being built.
//
// Move a grade into this list the moment its layouts are seeded. Nothing else
// needs to change: the portal is fully dynamic, so a seeded grade works the
// instant the Firestore documents exist.
export const GRADES_WITH_CONTENT = [
  "k", "1st", "2nd", "3rd", "4th", "5th", "6th", "7th", "8th",
];

// Science now runs K-8. Kindergarten has always had NGSS content; 6th-8th
// arrived with the middle school standards import, and states that publish
// 6-8 as a band have those expanded onto each grade.
export const SCIENCE_GRADES_WITH_CONTENT = [
  "k", "1st", "2nd", "3rd", "4th", "5th", "6th", "7th", "8th",
];

// Math runs K-8 as a grade range, but read the caveat before trusting it.
//
// Kindergarten math is 8 topics against 22 K standards, most of it Grade 1
// and 2 media re-pointed at the K standards it genuinely teaches. 6th-8th have
// standards imported but no layouts seeded in any state yet. Both are listed
// so the grades are reachable and measurable; neither is a claim that the
// grade is full. The per-state, per-subject switch in `stateSettings` is what
// decides whether a state actually offers math — this list only says the
// grade exists.
export const MATH_GRADES_WITH_CONTENT = [
  "k", "1st", "2nd", "3rd", "4th", "5th", "6th", "7th", "8th",
];

export const hasContent = (grade) => GRADES_WITH_CONTENT.includes(grade);

// The URL/role token for a grade: "Kindergarten" -> "k", "6th" -> "6th".
//
// Role strings are composed as `${state}-${token}-${role}` and PrivateRoute
// matches them literally, so this mapping is load-bearing — a grade that
// tokenises differently in two places produces an account whose role matches
// no route in the app. That already happened once with "3" vs "3rd" (see the
// note at the top of this file).
//
// Kindergarten and Pre-K get short tokens because "kindergarten" in every
// layout ID and every role string is unwieldy, and "k" is what the curriculum
// files already use.
export const GRADE_TOKENS = {
  "Pre-K": "prek",
  Kindergarten: "k",
  "1st": "1st",
  "2nd": "2nd",
  "3rd": "3rd",
  "4th": "4th",
  "5th": "5th",
  "6th": "6th",
  "7th": "7th",
  "8th": "8th",
  "9th": "9th",
  "10th": "10th",
  "11th": "11th",
  "12th": "12th",
};

// Display order, for anywhere that sorts grades.
export const GRADE_ORDER = GRADES.reduce((acc, g, i) => {
  acc[GRADE_TOKENS[g]] = i;
  return acc;
}, {});
